import {
  CModal,
  CModalBody,
  CModalHeader,
  CSidebar,
  CSidebarNav
} from "@coreui/react"
import Close from "assets/icons/close.png"
import classNames from "classnames"
import MediaProvider from "components/common/Context/MediaContext"
import PropTypes from "prop-types"
import React, { useContext, useEffect } from "react"
import { FaWindowMaximize, FaWindowMinimize } from "react-icons/fa"

import { useTheme } from "../Context/ThemeContext"
import { GreyedOut } from "../display"
import CMSTypography, {
  StandardTypographyTypes
} from "../typography/CMSTypography"

export default function Drawer({
  isOpen,
  onClose,
  heading,
  headingTooltip,
  tabs,
  selectedTab,
  setSelectedTab,
  centerHeading,
  minimizable,
  minimized = false,
  setMinimized,
  children,
  isSmall = false,
  padding = true
}) {
  const { isSmallScreen } = useContext(MediaProvider)
  const theme = useTheme()

  useEffect(() => {
    const appContainer = document.getElementById("app-container")
    if (isOpen && appContainer) {
      const scrollY = document.documentElement.scrollTop
      appContainer.classList.add("position-fixed")
      appContainer.style.top = `-${scrollY}px`
    }

    return () => {
      const appContainer = document.getElementById("app-container")
      if (isOpen && appContainer) {
        const scrollY = appContainer.style.top
        appContainer.classList.remove("position-fixed")
        appContainer.style.top = ""
        document.documentElement.scrollTo(0, parseInt(scrollY || "0") * -1)
      }
    }
  }, [isOpen])

  const drawerClass = classNames("c-sidebar-light", {
    "w-0": !isOpen,
    "top-unset": minimized,
    "minimized-drawer": isOpen && minimized,
    "c-sidebar-small": isSmall && isOpen
  })

  const drawerTabClass = classNames("drawer-tab align-items-center", {
    "drawer-tab-minimized": minimized
  })

  if (isSmallScreen)
    return (
      <CModal
        show={isOpen}
        onClose={onClose}
        centered
        addContentClass="drawer-mobile"
      >
        <CModalHeader closeButton>{heading}</CModalHeader>
        <CModalBody className="overflow-auto">{isOpen && children}</CModalBody>
      </CModal>
    )

  return (
    <GreyedOut enable={isOpen && !minimized} setIsOpen={onClose}>
      <CSidebar
        minimize={!isOpen}
        size={minimized ? "" : "xl"}
        aside
        className={drawerClass}
      >
        <CSidebarNav>
          <div className={drawerTabClass}>
            {tabs ? (
              <div className="d-flex cursor-pointer">
                {tabs.map((item) => (
                  <div
                    className={classNames("drawer-tab-btn", {
                      "green-bottom-border": selectedTab === item
                    })}
                    tabIndex={0}
                    onClick={() => setSelectedTab(item)}
                    key={item}
                  >
                    {item}
                  </div>
                ))}
              </div>
            ) : (
              <div className="drawer-tab-btn d-flex">
                <CMSTypography
                  type={StandardTypographyTypes.cardHeading}
                  color={theme.whiteColor}
                >
                  {heading}
                </CMSTypography>
                {headingTooltip}
              </div>
            )}
            <div className="d-flex">
              {centerHeading}
              <div className="close-btn">
                {minimizable &&
                  (minimized ? (
                    <FaWindowMaximize
                      tabIndex={0}
                      onClick={() => setMinimized(false)}
                      className="maximize-icon text-white mr-3"
                    />
                  ) : (
                    <FaWindowMinimize
                      tabIndex={0}
                      onClick={() => setMinimized(true)}
                      className="close-icon text-white mr-3"
                    />
                  ))}
                <img
                  src={Close}
                  alt="Close"
                  tabIndex={0}
                  onClick={onClose}
                  className="close-icon"
                />
              </div>
            </div>
          </div>
          {!minimized && isOpen ? (
            <div
              className={`${
                padding ? "drawer-body-padding" : ""
              } d-flex flex-column h-100`}
            >
              {children}
            </div>
          ) : null}
        </CSidebarNav>
      </CSidebar>
    </GreyedOut>
  )
}

Drawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  heading: PropTypes.node,
  headingTooltip: PropTypes.node,
  tabs: PropTypes.array,
  selectedTab: PropTypes.string,
  setSelectedTab: PropTypes.func,
  centerHeading: PropTypes.node,
  minimizable: PropTypes.bool,
  minimized: PropTypes.bool,
  setMinimized: PropTypes.func,
  children: PropTypes.node.isRequired,
  isSmall: PropTypes.bool,
  padding: PropTypes.bool
}
